import { useNotification } from '@/hooks';
import { NotificationSeverity } from '@/components/notifications/Notification';
import {
  useCreatePickupCycleMutation,
  useCreatePickupCyclesBulkMutation,
  useDeletePickupCycleMutation,
} from '@/pages/Business/pages/PickupCycles/services';
import {
  BulkCreatePickupCyclePayload,
  CreatePickupCyclePayload,
  PickupCycle,
} from '@/pages/Business/pages/PickupCycles/models';
import {
  formatCycleTime,
  getApiErrorMessage,
} from '@/pages/Business/pages/PickupCycles/utils';

const buildCycleKey = (
  commerceId: string,
  branchId: string | null,
  cycleTime: string,
) => `${commerceId}|${branchId ?? ''}|${formatCycleTime(cycleTime)}`;

export const usePickupCycleActions = () => {
  const { showNotification } = useNotification();
  const [createPickupCycle, { isLoading: isCreating }] =
    useCreatePickupCycleMutation();
  const [createPickupCyclesBulk, { isLoading: isCreatingBulk }] =
    useCreatePickupCyclesBulkMutation();
  const [deletePickupCycle, { isLoading: isDeleting }] =
    useDeletePickupCycleMutation();

  // Alta para una o varias sucursales. Las que ya tenían el retiro vuelven en
  // `skipped`: no es un error, se avisa y las demás quedan creadas igual.
  const handleCreateBulk = async (
    payload: BulkCreatePickupCyclePayload,
    onSuccess?: () => void,
  ) => {
    try {
      const { created, skipped } = await createPickupCyclesBulk(payload).unwrap();

      if (created.length === 0 && skipped.length > 0) {
        showNotification(
          'Esas sucursales ya tenían un retiro para esa hora',
          NotificationSeverity.warning,
        );
      } else if (skipped.length > 0) {
        showNotification(
          `${created.length} retiros creados. ${skipped.length} ya existían.`,
          NotificationSeverity.warning,
        );
      } else {
        showNotification(
          created.length === 1
            ? 'Retiro creado'
            : `${created.length} retiros creados`,
          NotificationSeverity.success,
        );
      }

      onSuccess?.();
    } catch (error) {
      showNotification(
        getApiErrorMessage(error, 'No se pudieron crear los retiros'),
        NotificationSeverity.error,
      );
    }
  };

  const handleDelete = async (id: string, onSuccess?: () => void) => {
    try {
      await deletePickupCycle({ id }).unwrap();
      showNotification('Retiro borrado', NotificationSeverity.success);
      onSuccess?.();
    } catch (error) {
      showNotification(
        getApiErrorMessage(error, 'No se pudo borrar el retiro'),
        NotificationSeverity.error,
      );
    }
  };

  // TODO: reemplazar por PUT /business/pickup-cycles/:id cuando el backend lo
  // agregue. Hoy "editar" = crear el ciclo nuevo y borrar el viejo.
  const handleEdit = async (
    oldCycle: PickupCycle,
    payload: CreatePickupCyclePayload,
    onSuccess?: () => void,
  ) => {
    const oldKey = buildCycleKey(
      oldCycle.commerceId,
      oldCycle.branchId,
      oldCycle.cycleTime,
    );
    const newKey = buildCycleKey(
      payload.commerceId,
      payload.branchId,
      payload.cycleTime,
    );
    const sameUniqueCombination = oldKey === newKey;

    if (sameUniqueCombination) {
      // Misma combinación comercio + sucursal + hora: si creáramos primero
      // chocaría con la regla de único, así que borramos primero y creamos
      // después.
      try {
        await deletePickupCycle({ id: oldCycle.id }).unwrap();
      } catch (error) {
        showNotification(
          getApiErrorMessage(error, 'No se pudo actualizar el retiro'),
          NotificationSeverity.error,
        );
        return;
      }

      try {
        await createPickupCycle(payload).unwrap();
        showNotification('Retiro actualizado', NotificationSeverity.success);
        onSuccess?.();
      } catch (error) {
        showNotification(
          `⚠️ El retiro anterior se borró pero no se pudo crear el nuevo: ${getApiErrorMessage(
            error,
            'error desconocido',
          )}. Volvé a crearlo.`,
          NotificationSeverity.error,
        );
      }
      return;
    }

    // Combinación distinta: creamos primero para no perder el ciclo si algo
    // falla, y solo si el POST sale OK borramos el viejo.
    try {
      await createPickupCycle(payload).unwrap();
    } catch (error) {
      showNotification(
        getApiErrorMessage(error, 'No se pudo actualizar el retiro'),
        NotificationSeverity.error,
      );
      return;
    }

    try {
      await deletePickupCycle({ id: oldCycle.id }).unwrap();
    } catch {
      // El ciclo nuevo ya quedó creado; avisamos pero no es un fallo total.
      showNotification(
        'El retiro nuevo se creó, pero no se pudo borrar el anterior. Borralo manualmente.',
        NotificationSeverity.warning,
      );
      onSuccess?.();
      return;
    }

    showNotification('Retiro actualizado', NotificationSeverity.success);
    onSuccess?.();
  };

  return {
    handleCreateBulk,
    handleDelete,
    handleEdit,
    isSubmitting: isCreating || isCreatingBulk || isDeleting,
  };
};
